import { getCurrentMenuId, getRestaurantId } from 'redux/app';
import { getBrandCurrency } from 'redux/brand';
import { useGetMenuHashMapQuery } from 'services/menu/menu.endpoints';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { useCallback, useEffect } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { updateMenuState } from 'redux/menu';
import { getOrderProductCount, getOrderTotalInclTax } from 'redux/order';
import { computePrice } from 'utils/price';
import { Routes } from 'core/routes.types';
import { useTranslation } from 'react-i18next';
import { updateConsumptionModeList } from 'redux/consumptionModeList';
import { useConsumptionModeListQuery } from 'services/consumptionMode/consumptionMode.endpoints';
import { ChannelId } from 'services/order/order.type';

export const useMenuHomeVM = () => {
    const dispatch = useAppDispatch();
    const { i18n } = useTranslation();
    const navigate = useNavigate();

    const currentMenuId = useAppSelector(getCurrentMenuId);
    const restaurantId = useAppSelector(getRestaurantId);
    const currency = useAppSelector(getBrandCurrency);
    const productCount = useAppSelector(getOrderProductCount);
    const totalInclTax = useAppSelector(getOrderTotalInclTax);

    const navigateToProduct = useCallback(
        (id: number) =>
            navigate(
                generatePath(Routes.Product, {
                    productId: id.toString(),
                }),
            ),
        [navigate],
    );

    const navigateToCart = useCallback(() => navigate(Routes.Cart), [navigate]);

    const { isFetching, isError, data } = useGetMenuHashMapQuery(
        { menuId: currentMenuId || undefined },
        { skip: !currentMenuId },
    );

    const { data: consumptionModeList } = useConsumptionModeListQuery(
        { channelId: ChannelId.WEB, restaurantId: restaurantId || undefined },
        { skip: !restaurantId },
    );

    useEffect(() => {
        if (consumptionModeList) {
            dispatch(updateConsumptionModeList(consumptionModeList));
        }
    }, [consumptionModeList, restaurantId, dispatch]);

    useEffect(() => {
        if (data) {
            dispatch(updateMenuState(data));
        }
    }, [data, currentMenuId, dispatch]);

    return {
        isError: isError || !currentMenuId,
        isFetching,
        data,
        currency: currency ?? 'EUR',
        productCount,
        amountWithCurrency: currency ? computePrice(i18n, totalInclTax, currency) : '',
        navigateToProduct,
        navigateToCart,
    };
};
